import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { Locale } from './locales';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from './locales';
import { getTranslation } from './translations';
import type { TranslationKeys } from './translations/en';

interface I18nContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: TranslationKeys;
}

const I18nContext = createContext<I18nContextType | null>(null);

const LOCALE_STORAGE_KEY = 'pipelet-locale';

function getStoredLocale(): Locale {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored && SUPPORTED_LOCALES.includes(stored as Locale)) {
      return stored as Locale;
    }
  } catch (e) {
    console.warn('Failed to read locale from localStorage:', e);
  }
  return DEFAULT_LOCALE;
}

function setStoredLocale(locale: Locale): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch (e) {
    console.warn('Failed to save locale to localStorage:', e);
  }
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(getStoredLocale);
  const [t, setT] = useState<TranslationKeys>(() => getTranslation(locale));

  const setLocale = (newLocale: Locale) => {
    if (SUPPORTED_LOCALES.includes(newLocale)) {
      setLocaleState(newLocale);
      setStoredLocale(newLocale);
      setT(getTranslation(newLocale));
    }
  };

  useEffect(() => {
    setT(getTranslation(locale));
  }, [locale]);

  return <I18nContext.Provider value={{ locale, setLocale, t }}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
}

export function useTranslation() {
  const { t } = useI18n();
  return t;
}
